import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';

const normalizeTargetSchema = (targetSchema) => {
  if (!targetSchema) return [];
  if (Array.isArray(targetSchema)) return targetSchema;
  return [{ tableName: targetSchema, columns: [], id: targetSchema }];
};

const AGGREGATE_OPERATIONS = [
  { value: 'SUM', label: 'Sum' },
  { value: 'COUNT', label: 'Count' },
  { value: 'MIN', label: 'Minimum' },
  { value: 'MAX', label: 'Maximum' },
  { value: 'AVG', label: 'Average' },
];

const AggregateMappingDialog = ({
  open,
  onClose,
  sources = [],
  targetSchema,
  fetchedHeader = [],
  initialValues = null,
  existingMappings = [],
  onSave,
  reviewMode = false,
  onReject,
  onApprove,
}) => {
  const targets = useMemo(() => normalizeTargetSchema(targetSchema), [targetSchema]);

  const [sourceTable, setSourceTable] = useState(initialValues?.sourceTable || '');
  const [groupByColumns, setGroupByColumns] = useState(initialValues?.groupByColumns || []);
  const [aggregateColumn, setAggregateColumn] = useState(initialValues?.aggregateCol || '');
  const [operation, setOperation] = useState(initialValues?.aggregateType || 'SUM');
  const [targetTable, setTargetTable] = useState(initialValues?.targetTable || '');
  const [targetColumn, setTargetColumn] = useState(initialValues?.targetCol || '');

  useEffect(() => {
    if (open) {
      setSourceTable(initialValues?.sourceTable || '');
      setGroupByColumns(initialValues?.groupByColumns || []);
      setAggregateColumn(initialValues?.aggregateCol || '');
      setOperation(initialValues?.aggregateType || 'SUM');
      setTargetTable(initialValues?.targetTable || '');
      setTargetColumn(initialValues?.targetCol || '');
    }
  }, [open, initialValues]);

  const sourceTableOptions = useMemo(
    () => sources.map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources]
  );

  // Source columns for the selected table
  const sourceColumns = useMemo(() => {
    if (!sourceTable) return [];
    const src = sources.find((s) => (s.name || s.id) === sourceTable);
    const cols = src?.columns || [];
    return Array.isArray(cols) ? cols.map((c) => ({ value: c, label: c })) : [];
  }, [sources, sourceTable]);

  const targetTableOptions = useMemo(
    () => targets.map((t) => ({ value: t.tableName || t.id, label: t.tableName || t.id })),
    [targets]
  );

  const mappedTargetColsForTable = useMemo(() => {
    if (!targetTable || !Array.isArray(existingMappings)) return new Set();
    return new Set(
      existingMappings
        .filter((m) => (m.destination_name || m.destinationName) === targetTable)
        .map((m) => (m.target_column || m.targetColumn || '').trim())
        .filter(Boolean)
    );
  }, [targetTable, existingMappings]);

  // Target column options (exclude already-mapped columns; keep current selection when editing)
  const targetColumnOptions = useMemo(() => {
    if (!targetTable) return [];
    const target = targets.find((t) => (t.tableName || t.id) === targetTable);
    const normalizeCol = (c) => {
      if (c == null) return '';
      if (typeof c === 'string') return c;
      return c.column_name ?? c.name ?? c.columnName ?? '';
    };
    let opts = [];
    if (target?.columns?.length) {
      opts = target.columns
        .map((c) => {
          const name = normalizeCol(c);
          return { value: name, label: name };
        })
        .filter((opt) => opt.value != null && String(opt.value).trim() !== '');
    }
    if (opts.length === 0) {
      opts = (fetchedHeader || [])
        .filter((h) => h.sourceTable === targetTable)
        .map((h) => ({ value: h.columnName ?? h.column_name, label: h.columnName ?? h.column_name }))
        .filter((opt) => opt.value != null && opt.value !== '');
    }
    const currentSelection = initialValues?.targetCol ?? initialValues?.targetColumn ?? targetColumn;
    return opts.filter(
      (opt) => !mappedTargetColsForTable.has(opt.value) || opt.value === currentSelection
    );
  }, [targets, targetTable, fetchedHeader, mappedTargetColsForTable, initialValues?.targetCol, initialValues?.targetColumn, targetColumn]);

  const handleClose = () => {
    setSourceTable('');
    setGroupByColumns([]);
    setAggregateColumn('');
    setOperation('SUM');
    setTargetTable('');
    setTargetColumn('');
    onClose();
  };

  const handleSave = () => {
    if (
      !sourceTable ||
      groupByColumns.length === 0 ||
      !aggregateColumn ||
      !operation ||
      !targetTable ||
      !targetColumn
    ) {
      return;
    }

    onSave?.({
      sourceTable,
      groupByColumns,
      aggregateCol: aggregateColumn,
      aggregateType: operation,
      targetTable,
      targetCol: targetColumn,
      mapping_type: 'AGGREGATE',
    });
    handleClose();
  };

  const canSave =
    sourceTable && groupByColumns.length > 0 && aggregateColumn && operation && targetTable && targetColumn;

  const selectSx = {
    minWidth: 160,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fafafa',
      '&:hover': { backgroundColor: '#f5f5f5' },
      '&.Mui-focused': { backgroundColor: '#fff' },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#1f2937',
          borderBottom: '1px solid #e5e7eb',
          py: 2,
          px: 3,
          backgroundColor: '#fafafa',
        }}
      >
        Aggregate Mapping
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Source Section */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              SOURCE
            </Typography>

            {/* Source Table Selection */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="aggregate-source-table">Source table</InputLabel>
                <Select
                  labelId="aggregate-source-table"
                  value={sourceTable}
                  label="Source table"
                  onChange={(e) => {
                    setSourceTable(e.target.value);
                    setGroupByColumns([]);
                    setAggregateColumn('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {sourceTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Aggregate Column and Operation */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{ flex: 2, ...selectSx }}>
                <InputLabel id="aggregate-column">Aggregate column</InputLabel>
                <Select
                  labelId="aggregate-column"
                  value={aggregateColumn}
                  label="Aggregate column"
                  onChange={(e) => setAggregateColumn(e.target.value)}
                  disabled={reviewMode || !sourceTable}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 3, mt: 1.5 } } }}
                >
                  {sourceColumns.map((col) => (
                    <MenuItem key={col.value} value={col.value}>
                      {col.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1, ...selectSx }}>
                <InputLabel id="operation">Operation</InputLabel>
                <Select
                  labelId="operation"
                  value={operation}
                  label="Operation"
                  onChange={(e) => setOperation(e.target.value)}
                  disabled={reviewMode || !aggregateColumn}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {AGGREGATE_OPERATIONS.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Group By Columns (Multi-select) */}
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" fullWidth sx={selectSx}>
              <InputLabel id="group-by-columns">Group by columns</InputLabel>
              <Select
                labelId="group-by-columns"
                multiple
                value={groupByColumns}
                onChange={(e) => setGroupByColumns(e.target.value)}
                input={<OutlinedInput label="Group by columns" />}
                disabled={reviewMode || !sourceTable}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          backgroundColor: '#9398dd',
                          color: '#fff',
                          fontSize: '0.75rem',
                          '& .MuiChip-deleteIcon': { color: '#fff', fontSize: '18px' },
                        }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 1.5,
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {sourceColumns.map((col) => (
                  <MenuItem key={col.value} value={col.value}>
                    <Checkbox checked={groupByColumns.indexOf(col.value) > -1} size="small" />
                    <ListItemText primary={col.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              Select one or more columns to group by
            </Typography>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 1 }}>
            <Chip
              label="MAP TO TARGET"
              size="small"
              sx={{
                borderRadius: 1.5,
                backgroundColor: '#f0f0f0',
                color: '#1f2937',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            />
          </Divider>

          {/* Target Section */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              TARGET
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="aggregate-target-table">Target table</InputLabel>
                <Select
                  labelId="aggregate-target-table"
                  value={targetTable}
                  label="Target table"
                  onChange={(e) => {
                    setTargetTable(e.target.value);
                    setTargetColumn('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {targetTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="aggregate-target-col">Target column</InputLabel>
                <Select
                  labelId="aggregate-target-col"
                  value={targetColumn ?? ''}
                  label="Target column"
                  onChange={(e) => setTargetColumn(e.target.value)}
                  disabled={reviewMode || !targetTable}
                  MenuProps={{
                    PaperProps: { sx: { borderRadius: 2, mt: 1.5 } },
                    disableScrollLock: true,
                  }}
                >
                  {targetTable && targetColumnOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No columns available
                    </MenuItem>
                  ) : (
                    targetColumnOptions.map((opt, idx) => (
                      <MenuItem key={`targetcol-${idx}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Preview Section */}
          {canSave && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                backgroundColor: '#f9f9f9',
                border: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                AGGREGATE MAPPING PREVIEW
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`FROM: ${sourceTable}`}
                  size="small"
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1.5 }}
                />
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  GROUP BY
                </Typography>
                {groupByColumns.map((col) => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    sx={{ backgroundColor: '#9398dd', color: '#fff', borderRadius: 1.5 }}
                  />
                ))}
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  →
                </Typography>
                <Chip
                  label={`${operation}(${aggregateColumn})`}
                  size="small"
                  sx={{ backgroundColor: '#4caf50', color: '#fff', borderRadius: 1.5 }}
                />
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  →
                </Typography>
                <Chip
                  label={`TO: ${targetTable}.${targetColumn}`}
                  size="small"
                  sx={{ backgroundColor: '#ff9800', color: '#fff', borderRadius: 1.5 }}
                />
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
        }}
      >
        {reviewMode ? (
          <>
            <Button onClick={onReject} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' } }}>
              Reject
            </Button>
            <Button onClick={onApprove} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#16a34a', color: '#fff', '&:hover': { bgcolor: '#15803d' } }}>
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5 }}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={!canSave} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#00043c', color: '#fff', boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)', '&:hover': { bgcolor: '#000335', boxShadow: '0 4px 12px rgba(0, 4, 60, 0.4)' } }}>
              {initialValues ? 'Update' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AggregateMappingDialog;
